<!-- ============================================================================= -->
<!-- Ella Custom JS - Customize The Style For Layout -->
<!-- ============================================================================= -->

<!-- ============================================================================= -->
<!-- IMPORTANT DISCLAIMER -->
<!-- Please use only JS to style the layout. -->
<!-- ============================================================================= -->
(() => {
  'use strict';

  const GIFT_URL = 'https://vrindaam.com/pages/offers-gifts';

  const GIFT_TIERS = [
    {
      id: 'tier-1',
      minimum: 199900,
      value: 34500,
      title: 'Varnya Rose & Clay Soap',
      couponCode: 'FREESOAP',
      giftUrl: GIFT_URL
    },
    {
      id: 'tier-2',
      minimum: 299900,
      value: 50500,
      title: 'Chandrakanti Lip Oil',
      couponCode: 'FREELIPOIL',
      giftUrl: GIFT_URL
    },
    {
      id: 'tier-3',
      minimum: 499900,
      value: 131500,
      title: 'Nidra Sleep Kit',
      couponCode: 'FREESLEEPKIT',
      giftUrl: GIFT_URL
    },
    {
      id: 'tier-4',
      minimum: 799900,
      value: 239000,
      title: 'Madhuvan Body Roll On Gift Box',
      couponCode: 'FREEGIFTBOX',
      giftUrl: GIFT_URL
    }
  ];

  let latestCart = null;
  let renderTimer = null;
  let observer = null;
  let observedWrapper = null;

  /*
   * ==========================================
   * COUNTRY DETECTION
   * ==========================================
   *
   * Shopify Markets country can be exposed in
   * different ways depending on the theme.
   *
   * We check several possible Shopify values.
   */

  function getCustomerCountry() {

    /* Shopify Markets / Liquid injected value */
    if (window.VRINDAAM_COUNTRY) {
      return String(window.VRINDAAM_COUNTRY).toUpperCase();
    }

    /* Shopify global */
    if (window.Shopify?.country) {
      return String(window.Shopify.country).toUpperCase();
    }

    /* Shopify localization */
    if (window.Shopify?.locale?.country) {
      return String(
        window.Shopify.locale.country
      ).toUpperCase();
    }

    /* HTML country attribute if theme provides one */
    const htmlCountry =
      document.documentElement.getAttribute(
        'data-country'
      );

    if (htmlCountry) {
      return String(htmlCountry).toUpperCase();
    }

    return '';
  }

  function isIndianCustomer() {
    return getCustomerCountry() === 'IN';
  }

  /*
   * ==========================================
   * ROOT URL
   * ==========================================
   */

  function rootUrl() {
    const root =
      window.routes?.root ||
      window.Shopify?.routes?.root ||
      '/';

    return root.endsWith('/')
      ? root
      : `${root}/`;
  }

  /*
   * ==========================================
   * GET CART
   * ==========================================
   */

  async function getCart() {

    const response = await fetch(
      `${rootUrl()}cart.js`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load the cart.'
      );
    }

    return response.json();
  }

  /*
   * ==========================================
   * GET CURRENT GIFT TIER
   * ==========================================
   */

  function getTier(cart) {

    return [...GIFT_TIERS]
      .reverse()
      .find(
        (tier) =>
          Number(cart.total_price) >=
          tier.minimum
      ) || null;
  }

  /*
   * ==========================================
   * GET NEXT GIFT TIER
   * ==========================================
   */

  function getNextTier(currentTier) {

    if (!currentTier) {
      return GIFT_TIERS[0];
    }

    const currentIndex =
      GIFT_TIERS.findIndex(
        (tier) =>
          tier.id === currentTier.id
      );

    return (
      GIFT_TIERS[currentIndex + 1] ||
      null
    );
  }

  /*
   * ==========================================
   * FORMAT MONEY
   * ==========================================
   */

  function formatMoney(paise) {

    return new Intl.NumberFormat(
      'en-IN',
      {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }
    ).format(paise / 100);
  }

  /*
   * ==========================================
   * CART DRAWER WRAPPER
   * ==========================================
   */

  function getWrapper() {

    return document.querySelector(
      '#halo-cart-sidebar .previewCart-wrapper'
    );
  }

  /*
   * ==========================================
   * OBSERVE CART DRAWER
   * ==========================================
   */

  function startObservingDrawer() {

    const wrapper = getWrapper();

    if (
      !wrapper ||
      wrapper === observedWrapper
    ) {
      return;
    }

    observer?.disconnect();

    observedWrapper = wrapper;

    observer =
      new MutationObserver(() => {

        if (
          latestCart &&
          isIndianCustomer()
        ) {
          scheduleRender(latestCart);
        }

      });

    observer.observe(
      wrapper,
      {
        childList: true
      }
    );
  }

  /*
   * ==========================================
   * SCHEDULE RENDER
   * ==========================================
   */

  function scheduleRender(cart) {

    latestCart = cart;

    clearTimeout(renderTimer);

    renderTimer =
      window.setTimeout(() => {

        renderTierGiftUI(
          latestCart
        );

      }, 100);
  }

  /*
   * ==========================================
   * REMOVE GIFT UI
   * ==========================================
   */

  function removeTierGiftUI() {

    document
      .querySelectorAll(
        '.vrindaam-tier-gift-ui'
      )
      .forEach(
        (element) => element.remove()
      );
  }

  /*
   * ==========================================
   * CREATE GIFT CARD
   * ==========================================
   */

  function createGiftCard(tier) {

    const card =
      document.createElement('div');

    card.className =
      'vrindaam-tier-gift-card';

    const icon =
      document.createElement('span');

    icon.className =
      'vrindaam-tier-gift-card__icon';

    icon.setAttribute(
      'aria-hidden',
      'true'
    );

    icon.textContent = '🎁';

    const content =
      document.createElement('div');

    content.className =
      'vrindaam-tier-gift-card__content';

    const line1 =
      document.createElement('p');

    line1.className =
      'vrindaam-tier-gift-card__line';

    line1.innerHTML = `
      Get "<a
        href="${tier.giftUrl}"
        target="_blank"
        rel="noopener"
      >${tier.title}</a>"
      worth
      <strong>
        ${formatMoney(tier.value)}
      </strong>
      for free
    `;

    const line2 =
      document.createElement('p');

    line2.className =
      'vrindaam-tier-gift-card__line';

    line2.innerHTML = `
      Add "<a
        href="${tier.giftUrl}"
        target="_blank"
        rel="noopener"
      >${tier.title}</a>"
      to cart & apply coupon code
      <strong>
        ${tier.couponCode}
      </strong>
      at checkout
    `;

    content.append(
      line1,
      line2
    );

    card.append(
      icon,
      content
    );

    return card;
  }

  /*
   * ==========================================
   * CREATE PROGRESS MESSAGE
   * ==========================================
   */

  function createProgressMessage(
    nextTier,
    cartTotal
  ) {

    if (!nextTier) {
      return null;
    }

    const amountRemaining =
      Math.max(
        nextTier.minimum -
        Number(cartTotal),
        0
      );

    const progress =
      document.createElement('div');

    progress.className =
      'vrindaam-tier-gift-progress';

    progress.innerHTML = `
      Add
      <strong>
        ${formatMoney(amountRemaining)}
      </strong>
      more to get
      <strong>
        ${nextTier.title}
      </strong>
      worth
      <strong>
        ${formatMoney(nextTier.value)}
      </strong>
      free.
    `;

    return progress;
  }

  /*
   * ==========================================
   * RENDER GIFT UI
   * ==========================================
   */

  function renderTierGiftUI(cart) {

    /*
     * IMPORTANT:
     *
     * Only the gift promotion is removed.
     * The actual cart drawer is NOT touched.
     */

    if (!isIndianCustomer()) {

      removeTierGiftUI();

      return;
    }

    startObservingDrawer();

    const wrapper =
      getWrapper();

    if (!wrapper) {
      return;
    }

    /*
     * Empty cart
     */

    if (
      Number(cart.item_count) === 0
    ) {

      removeTierGiftUI();

      return;
    }

    const currentTier =
      getTier(cart);

    const nextTier =
      getNextTier(currentTier);

    const amountRemaining =
      nextTier
        ? Math.max(
            nextTier.minimum -
            Number(cart.total_price),
            0
          )
        : 0;

    const currentState = [
      currentTier?.id ||
        'no-tier',

      nextTier?.id ||
        'final-tier',

      amountRemaining

    ].join('|');

    const existingUI =
      wrapper.querySelector(
        '.vrindaam-tier-gift-ui'
      );

    /*
     * Nothing changed.
     */

    if (
      existingUI?.dataset.state ===
      currentState
    ) {
      return;
    }

    removeTierGiftUI();

    const ui =
      document.createElement('div');

    ui.className =
      'vrindaam-tier-gift-ui';

    ui.dataset.state =
      currentState;

    /*
     * Current gift
     */

    if (currentTier) {

      ui.appendChild(
        createGiftCard(
          currentTier
        )
      );

    }

    /*
     * Next gift progress
     */

    const progress =
      createProgressMessage(
        nextTier,
        cart.total_price
      );

    if (progress) {

      ui.appendChild(
        progress
      );

    }

    /*
     * Add gift UI to top
     * of cart drawer.
     */

    wrapper.prepend(ui);
  }

  /*
   * ==========================================
   * CART UPDATE EVENT
   * ==========================================
   */

  document.addEventListener(
    'cart-update',
    (event) => {

      /*
       * Non-Indian customer:
       * remove ONLY the gift UI.
       */

      if (!isIndianCustomer()) {

        removeTierGiftUI();

        return;
      }

      if (event.detail) {

        scheduleRender(
          event.detail
        );

      } else {

        /*
         * If the event does not contain
         * cart data, fetch the latest cart.
         */

        getCart()
          .then(scheduleRender)
          .catch(() => {});

      }
    }
  );

  /*
   * ==========================================
   * INITIALISE
   * ==========================================
   */

  async function initialise() {

    /*
     * Wait briefly for Shopify/theme
     * objects to become available.
     */

    let attempts = 0;

    while (
      !getCustomerCountry() &&
      attempts < 20
    ) {

      await new Promise(
        (resolve) =>
          setTimeout(resolve, 100)
      );

      attempts++;
    }

    /*
     * If country is not India,
     * remove only our promotional UI.
     */

    if (!isIndianCustomer()) {

      removeTierGiftUI();

      return;
    }

    /*
     * India customer
     */

    startObservingDrawer();

    try {

      const cart =
        await getCart();

      scheduleRender(
        cart
      );

    } catch (error) {

      console.error(
        'Vrindaam tier gift script failed:',
        error
      );

    }
  }

  /*
   * ==========================================
   * START SCRIPT
   * ==========================================
   */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      {
        once: true
      }
    );

  } else {

    initialise();

  }

})();